import { useCallback, useEffect, useState } from 'react'
import { Coordinates, FetchStoresError, StoreWithDistance } from './types'
import { geocode } from '../services/google-maps-service'
import { mapSitesToStores } from '../utils/map-sites-to-stores'
import { getDistance } from 'geolib'
import { getSites } from '../services/cinch-servicing'
import { datadogRum } from '@datadog/browser-rum'

export const useFetchAllStoresAndCalculateDistance = (postcode?: string) => {
  const [storesWithDistance, setStoresWithDistance] = useState<
    StoreWithDistance[]
  >([])
  const [customerCoordinates, setCustomerCoordinates] = useState<
    Coordinates | undefined
  >()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FetchStoresError | null>(null)

  useEffect(() => {
    if (postcode) {
      fetchAllStoresAndCalculateDistances(postcode)
    }
  }, [postcode])

  const fetchAllStoresAndCalculateDistances = useCallback(
    async (postcode: string) => {
      setLoading(true)
      setError(null)
      try {
        const customerCoordinates = await geocode(postcode)
        if (!customerCoordinates) {
          datadogRum.addError(new Error('Cinch Servicing: Postcode Error'), {
            cinchServicing: true,
            postcode,
          })
          setError('Postcode')
        } else {
          const sites = await getSites()
          if (!sites) {
            throw new Error('Error retrieving stores')
          }
          const stores = mapSitesToStores(sites)

          const updatedStores = stores
            .map((store) => {
              const distance = calculateStoreDistance(
                customerCoordinates,
                store.coordinates,
              )
              return {
                store,
                distance,
              }
            })
            .sort((a, b) => a.distance - b.distance)
            .map((s, index) => ({ ...s, index }))

          setStoresWithDistance(updatedStores)
          setCustomerCoordinates({
            latitude: customerCoordinates.lat,
            longitude: customerCoordinates.lng,
          })
        }
      } catch {
        datadogRum.addError(new Error('Cinch Servicing: Stores Error'), {
          cinchServicing: true,
        })
        setError('Stores')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return {
    storesWithDistance,
    customerCoordinates,
    loading,
    error,
  }
}

const calculateStoreDistance = (
  customerCoordinates: { lat: number; lng: number },
  nearestCollectionSite: {
    longitude: number
    latitude: number
  },
): number => {
  const { latitude, longitude } = nearestCollectionSite
  return Math.floor(
    getDistance(
      { latitude: customerCoordinates.lat, longitude: customerCoordinates.lng },
      { latitude, longitude },
    ),
  )
}
